import type { ListResponse, Meta, QueryParams } from '@/requests/types';
import type { BaseInstance, ModelName } from '@pigello/pigello-matrix';
import { getListNested } from '../api/get-list-nested';
import { MAX_PAGE_SIZE } from '../constants';
import { customCaseHandlers } from '../hooks/custom-cases';

import pLimit from 'p-limit';

const limit = pLimit(8);

export async function fetchAllInstances<Instance extends BaseInstance>({
  modelName,
  filters,
  order,
  nested,
  slim,
  useCustomNestedCaseHandlers,
  fetchAllManyRelations,
}: {
  modelName: ModelName;
  filters: QueryParams<Instance>['filters'];
  order?: QueryParams<Instance>['order'];
  nested?: (keyof Instance)[];
  slim?: boolean;
  useCustomNestedCaseHandlers?: boolean;
  fetchAllManyRelations?: boolean;
}) {
  // check how many pages first
  const pageSizeData = await getListNested<Instance>({
    modelName,
    queryParams: {
      filters: {
        ...(filters ?? {}),
      },
      page: 1,
      pageSize: 1,
      order,
      slim,
    },
    nested,
  });

  const totalSize = pageSizeData.meta.total_amount;

  const pageAmount =
    totalSize < MAX_PAGE_SIZE ? 1 : Math.ceil(totalSize / MAX_PAGE_SIZE);

  const promises: Promise<{
    list: BaseInstance[];
    meta: Meta;
  }>[] = [];

  for (let i = 1; i <= pageAmount; i++) {
    promises.push(
      limit(() =>
        getListNested<Instance>({
          modelName,
          queryParams: {
            filters: {
              ...(filters ?? {}),
            },
            page: i,
            pageSize: MAX_PAGE_SIZE,
            order,
            slim,
          },
          nested,
          fetchAllManyRelations,
        })
      )
    );
  }

  const result = await Promise.allSettled(promises);

  const succeededResults = (
    result as unknown as {
      value: ListResponse<Instance>;
      status: 'fulfilled' | 'rejected';
    }[]
  ).filter((r) => r.status === 'fulfilled');

  const handler = customCaseHandlers[modelName];
  if (useCustomNestedCaseHandlers && handler) {
    try {
      const flattenedList = succeededResults.flatMap((r) => r.value.list);
      const processedList = await handler({
        data: flattenedList,
      });
      return {
        list: processedList.data,
        meta: {
          page: 1,
          page_size: processedList.data.length,
          total_amount: processedList.data.length,
          page_amount: 1,
        },
      };
    } catch {
      console.error(
        'Error in custom case handler fetchAllInstances',
        modelName
      );
      const flattenedList = succeededResults.flatMap((r) => r.value.list);
      return {
        list: flattenedList,
        meta: {
          page: 1,
          page_amount: 1,
          page_size: flattenedList.length,
          total_amount: flattenedList.length,
        },
      };
    }
  }

  const flattenedList = succeededResults.flatMap((r) => r.value.list);
  return {
    list: flattenedList,
    meta: {
      page: 1,
      page_amount: 1,
      page_size: flattenedList.length,
      total_amount: flattenedList.length,
    },
  };
}
